import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ModalMessage from '../ModalMessage/ModalMessage';
import Icon from '../Icon/Icon';
import { getShippingMethods } from '../../Reducers/actions/shippingMethodsActions';
import { ShippingMethodSelectorRowSkeleton } from '../Skeletons/FinanceListSkeletons';
import './ShippingMethodSelector.scss';

const ShippingMethodSelector = ({ selectedMethodId = null, onSelect, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { methods, loading } = useSelector((state) => state.shippingMethods || { methods: [], loading: false });
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        // Only fetch if methods are empty and not currently loading
        if ((!methods || methods.length === 0) && !loading) {
            dispatch(getShippingMethods());
        }
    }, [dispatch]); // Remove methods and loading from dependencies to prevent infinite loop

    const filteredMethods = methods.filter(method => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        const title = method.title?.toLowerCase() || '';
        const destinations = method.destinations?.map(d => d.country_name || d.name || '').join(' ').toLowerCase() || '';
        return title.includes(query) || destinations.includes(query);
    });

    const handleSelectMethod = (methodId) => {
        // If clicking the same method, deselect it (set to null)
        if (selectedMethodId === methodId) {
            onSelect(null);
        } else {
            onSelect(methodId);
        }
    };

    const formatDestinations = (destinations) => {
        if (!destinations || destinations.length === 0) return 'No destination';
        if (destinations.length <= 2) {
            return destinations.map(d => d.country_name || d.name || d).join(', ');
        }
        return `${destinations.slice(0, 2).map(d => d.country_name || d.name || d).join(', ')} + ${destinations.length - 2} more`;
    };

    const formatRates = (rates) => {
        if (!rates || rates.length === 0) return 'No rates';
        const rateStrings = rates.map(rate => {
            let str = `€${parseFloat(rate.rate || 0).toFixed(2)}`;
            if (rate.additional_item_rate) {
                str += ` (+€${parseFloat(rate.additional_item_rate).toFixed(2)})`;
            }
            return str;
        });
        if (rateStrings.length <= 1) {
            return rateStrings.join(' ');
        }
        return `${rateStrings[0]} + ${rateStrings.length - 1} more`;
    };

    return (
        <ModalMessage onClose={onClose}>
            <div className="shipping-method-selector">
                <div className="shipping-method-selector-header">
                    <p className="fs-sm2 fw-bold fg-dark">Select Shipping Method</p>
                </div>

                {/* Search */}
                <div className="shipping-method-selector-search">
                    <div className="search-page__search-container">
                        <input
                            type="text"
                            className="search-page__search-input"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon 
                            alias="search_input_icon" 
                            width={20} 
                            stroke="#FF3232" 
                            color="none" 
                            stroke-linecap="round" 
                            stroke-linejoin="round" 
                            strokeWidth={2} 
                            className="search-page__search-icon" 
                        />
                        {searchQuery && (
                            <button
                                className="search-page__clear-button"
                                onClick={() => setSearchQuery('')}
                            >
                                <Icon alias="cross" width={12} color="$grey7" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Methods List */}
                <div className="shipping-method-selector-list">
                    {loading ? (
                        <div role="status" aria-label="Loading shipping methods">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <ShippingMethodSelectorRowSkeleton key={i} />
                            ))}
                        </div>
                    ) : filteredMethods.length === 0 ? (
                        <div className="no-methods">
                            <p className="fs-xs fg-dark fw-medium mb-16">
                                {searchQuery ? 'No methods found' : 'No shipping methods available. Create one in your dashboard.'}
                            </p>
                            {!searchQuery && (
                                <button
                                    className="btn red-btn btn-full"
                                    onClick={() => {
                                        onClose();
                                        navigate('/shipping-methods');
                                    }}
                                    style={{ height: '48px', marginTop: '8px',fontSize: '14px' }}
                                >
                                    ADD A NEW SHIPPING METHODS
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredMethods.map((method) => {
                            const methodId = method.shipping_method_id || method.id;
                            const isSelected = selectedMethodId === methodId;
                            
                            return (
                                <div
                                    key={methodId}
                                    className={`shipping-method-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleSelectMethod(methodId)}
                                >
                                    <div className="d-flex aic" style={{ flex: 1 }}>
                                        <div className="radio-wrapper" style={{ position: 'relative', marginRight: '12px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <input
                                                type="radio"
                                                name="shipping-method"
                                                checked={isSelected}
                                                onChange={() => handleSelectMethod(methodId)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="shipping-method-radio"
                                                style={{
                                                    appearance: 'none',
                                                    WebkitAppearance: 'none',
                                                    MozAppearance: 'none',
                                                    accentColor: 'transparent',
                                                    width: '24px',
                                                    height: '24px',
                                                    border: '2px solid #E5E5E5',
                                                    borderRadius: '50%',
                                                    backgroundColor: isSelected ? '#FF3232' : '#FFFFFF',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    margin: 0,
                                                    padding: 0
                                                }}
                                            />
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '12px',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '10px',
                                                        height: '10px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#FFFFFF',
                                                        pointerEvents: 'none',
                                                        zIndex: 2
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="method-info" style={{ flex: 1 }}>
                                            {method.type === 'specific' && method.title && (
                                                <p className="method-title fs-xs fw-bold fg-dark mb-4">{method.title}</p>
                                            )}
                                            <p className="method-destinations fs-xs fg-dark fw-medium mb-4">
                                                {formatDestinations(method.destinations)}
                                            </p>
                                            <p className="method-rates fs-xs fg-dark">
                                                {formatRates(method.rates)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Actions */}
                {!loading && filteredMethods.length > 0 && (
                    <div className="shipping-method-selector-actions">
                        <button
                            className="btn red-btn btn-full"
                            onClick={onClose}
                            style={{ height: '64px' }}
                        >
                            CONFIRM
                        </button>
                        <p
                            className="fs-xxs fg-dark fw-bold mb-16 mt-16 ta-center c-pointer td-underline"
                            onClick={onClose}
                        >
                            CANCEL
                        </p>
                    </div>
                )}
                {!loading && filteredMethods.length === 0 && (
                    <div className="shipping-method-selector-actions">
                        <p
                            className="fs-xxs fg-dark fw-bold mb-16 mt-16 ta-center c-pointer td-underline"
                            onClick={onClose}
                        >
                            CANCEL
                        </p>
                    </div>
                )}
            </div>
        </ModalMessage>
    );
};

export default ShippingMethodSelector;

